import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Search, Users } from 'lucide-react'
import { api } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { Avatar } from '../../components/ui/Avatar'
import { Button } from '../../components/ui/Button'
import { EmptyState } from '../../components/ui/EmptyState'
import { etichettaPiano, variantePiano, formatDataOra } from '../../lib/format'
import type { Utente } from '../../lib/tipi'
import pagina from '../pagine.module.css'

interface Risposta {
  utenti: Utente[]
  totale: number
  page: number
  per_pagina: number
}

export function AdminUtenti() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [cerca, setCerca] = useState('')
  const [ruolo, setRuolo] = useState('')
  const [piano, setPiano] = useState('')

  const q = useQuery({
    queryKey: ['admin', 'utenti', page, cerca, ruolo, piano],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, per_pagina: 25 }
      if (cerca) params.cerca = cerca
      if (ruolo) params.ruolo = ruolo
      if (piano) params.piano = piano
      return (await api.get<{ dati: Risposta }>('/superadmin/utenti', { params })).data.dati
    },
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento utenti…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento" />

  const colonne: Colonna<Utente>[] = [
    {
      chiave: 'utente', intestazione: 'Utente',
      render: (u) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar nome={u.nome} cognome={u.cognome} taglia="sm" />
          <div>
            <div style={{ fontWeight: 600 }}>{u.nome} {u.cognome}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{u.email}</div>
          </div>
        </div>
      ),
    },
    {
      chiave: 'ruolo', intestazione: 'Ruolo',
      render: (u) => (
        <Badge variante={u.ruolo === 'superadmin' ? 'accent' : u.ruolo === 'manager' ? 'info' : 'default'}>
          {u.ruolo}
        </Badge>
      ),
      larghezza: '120px',
    },
    {
      chiave: 'piano', intestazione: 'Piano',
      render: (u) => <Badge variante={variantePiano(u.piano)}>{etichettaPiano(u.piano)}</Badge>,
      larghezza: '110px',
    },
    {
      chiave: 'attivo', intestazione: 'Attivo',
      render: (u) =>
        u.attivo ? <Badge variante="success">Attivo</Badge> : <Badge variante="warning">Sospeso</Badge>,
      larghezza: '100px',
    },
    {
      chiave: 'sessioni', intestazione: 'Sessioni',
      allineamento: 'right',
      render: (u) => <span className="mono">{u.sessioniTotali}</span>,
      larghezza: '90px',
    },
    {
      chiave: 'ultimo', intestazione: 'Ultimo accesso',
      render: (u) => <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{formatDataOra(u.lastLogin)}</span>,
      larghezza: '170px',
    },
  ]

  const totalePagine = Math.max(1, Math.ceil(q.data.totale / q.data.per_pagina))

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Utenti</h1>
          <div className={pagina.sottotitolo}>{q.data.totale} utenti totali</div>
        </div>
      </div>

      <div className={pagina.filtri}>
        <div className={pagina.search}>
          <Search size={16} color="var(--text-muted)" />
          <input
            placeholder="Cerca nome o email…"
            value={cerca}
            onChange={(e) => { setCerca(e.target.value); setPage(1) }}
          />
        </div>
        <select className={pagina.select} value={ruolo} onChange={(e) => { setRuolo(e.target.value); setPage(1) }}>
          <option value="">Tutti i ruoli</option>
          <option value="sdr">SDR</option>
          <option value="manager">Manager</option>
          <option value="superadmin">Superadmin</option>
        </select>
        <select className={pagina.select} value={piano} onChange={(e) => { setPiano(e.target.value); setPage(1) }}>
          <option value="">Tutti i piani</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="team">Team</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>

      <Table
        colonne={colonne}
        righe={q.data.utenti}
        onClickRiga={(u) => navigate(`/admin/utenti/${u.id}`)}
        vuoto={<EmptyState icona={<Users size={40} />} titolo="Nessun utente trovato" />}
      />

      {totalePagine > 1 && (
        <div className={pagina.paginazione}>
          <span>Pagina {q.data.page} di {totalePagine}</span>
          <div className={pagina.paginazioneBottoni}>
            <Button taglia="sm" variante="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} iconaSinistra={<ChevronLeft size={14} />}>
              Indietro
            </Button>
            <Button taglia="sm" variante="secondary" disabled={page >= totalePagine} onClick={() => setPage(page + 1)} iconaDestra={<ChevronRight size={14} />}>
              Avanti
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
